import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MarkdownRenderer from '../MarkdownRenderer';
import { getScoreColors, getScoreColor } from '../../utils/colorUtils';
import { Hypothesis } from '../../types';
import { useApi } from '../../contexts/ApiContext';

interface HypothesisCardProps {
  hypothesis: Hypothesis;
  showProblemTitle?: boolean;
  problemTitle?: string;
  showProjectContext?: boolean;
  onClick?: (hypothesis: Hypothesis) => void;
  onUpdate?: (hypothesis: Hypothesis) => void;
}

const HypothesisCard: React.FC<HypothesisCardProps> = ({ 
  hypothesis, 
  showProblemTitle = false, 
  problemTitle,
  showProjectContext = false,
  onClick,
  onUpdate
}) => {
  const navigate = useNavigate();
  const { api } = useApi();
  const priorityScore = hypothesis.priority_score || 0;
  const [isToggling, setIsToggling] = useState(false);

  const handleClick = () => {
    if (onClick) {
      onClick(hypothesis);
    } else {
      navigate(`/app-hypotheses/${hypothesis.id}`);
    }
  };

  const handleToggleActive = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    setIsToggling(true);
    try {
      const response = await api.patch(`/hypotheses/${hypothesis.id}/toggle-active`);
      if (onUpdate) {
        onUpdate(response.data || response);
      }
    } catch (error) {
      console.error('Error toggling hypothesis active status:', error);
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <div 
      className={`${hypothesis.is_active === true ? 'bg-green-50 border-green-500 border-2' : 'bg-white border-l-4'} ${hypothesis.is_active === true ? 'border-green-500' : getScoreColors(priorityScore).borderLeft} shadow-sm rounded-r-lg hover:shadow-md transition-all duration-200`}
    >
      <button
        onClick={handleClick}
        className="text-left w-full p-6 hover:bg-gray-50 rounded-r-lg"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-medium text-gray-900 hover:text-indigo-600">{hypothesis.title}</h3>
              {hypothesis.is_active === true && (
                <span className="px-2 py-1 text-xs font-semibold bg-green-100 text-green-800 rounded-full">
                  🔸 ACTIVE
                </span>
              )}
            </div>
            <div className="mt-1 flex items-center space-x-2">
              {showProblemTitle && problemTitle && (
                <span className="text-sm text-gray-500">Problem: {problemTitle}</span>
              )}
              {showProjectContext && hypothesis.project_id && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/app-projects?view=detail&id=${hypothesis.project_id}`);
                  }}
                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-md border border-indigo-200"
                  title="View in project context"
                >
                  📋 Project {hypothesis.project_id}
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleToggleActive}
              disabled={isToggling}
              className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                hypothesis.is_active === true
                  ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              } ${isToggling ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Toggle active status"
            >
              {isToggling ? '...' : hypothesis.is_active === true ? '🔸 Active' : '⚫ Inactive'}
            </button>
            <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${
              `${getScoreColors(priorityScore).background} ${getScoreColors(priorityScore).text}`
            }`}>
              Score: {hypothesis.priority_score?.toFixed(2) || 'N/A'}
            </span>
          </div>
        </div>
        
        <MarkdownRenderer content={hypothesis.hypothesis_statement} className="mt-2 text-sm text-gray-600 leading-relaxed" />
        
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="flex flex-col">
            <span className="text-xs text-gray-500 uppercase tracking-wide">Impact</span>
            <span className={`text-lg font-bold ${getScoreColor(hypothesis.business_impact, 10, false)}`}>
              {hypothesis.business_impact}/10
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-gray-500 uppercase tracking-wide">Feasibility</span>
            <span className={`text-lg font-bold ${getScoreColor(hypothesis.technical_feasibility, 10, false)}`}>
              {hypothesis.technical_feasibility}/10
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-gray-500 uppercase tracking-wide">Strategy</span>
            <span className={`text-lg font-bold ${getScoreColor(hypothesis.strategy_alignment, 10, false)}`}>
              {hypothesis.strategy_alignment}/10
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-gray-500 uppercase tracking-wide">Effort</span>
            <span className={`text-lg font-bold ${getScoreColor(hypothesis.implementation_effort, 10, true)}`}>
              {hypothesis.implementation_effort}/10
            </span>
          </div>
        </div>
      </button>
    </div>
  );
};

export default HypothesisCard;